import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Skeleton,
  Space,
  Switch,
  Tag,
  Tooltip,
  Tree,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
  HolderOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import type { CategoryNode, StoreCatalogTree } from "./types";
import { HistorialDelCatalogo, TARJETA_CON_ENCABEZADO } from "./HistorialDelCatalogo";
import { ProductosDeLaCategoria } from "./ProductosDeLaCategoria";

const { Text, Title } = Typography;

const urlDeStore = (storeCode: string) =>
  `${API_BASE_URL}/pim/stores/${encodeURIComponent(storeCode)}/catalog`;

const nivelDe = (valor: unknown): string =>
  String(valor ?? "").trim().toLowerCase();

/** Aplana el árbol para poder buscar un nodo por id sin recorrerlo a mano cada vez. */
const indexar = (nodos: CategoryNode[]): Map<number, CategoryNode> => {
  const out = new Map<number, CategoryNode>();
  const recorrer = (lista: CategoryNode[]) => {
    for (const n of lista) {
      out.set(n.categoryId, n);
      recorrer(n.children);
    }
  };
  recorrer(nodos);
  return out;
};

/**
 * Devuelve el árbol con las hijas de `parentId` en el orden `orden` (ids). Copia sólo el
 * camino que cambia: es para mostrar el orden nuevo apenas se suelta, antes de que vuelva
 * el API (xcons-docs#455).
 */
const conHermanasEnOrden = (
  nodos: CategoryNode[],
  parentId: number,
  orden: number[],
): CategoryNode[] => {
  const ordenar = (lista: CategoryNode[]) =>
    orden
      .map((id) => lista.find((n) => n.categoryId === id))
      .filter((n): n is CategoryNode => !!n);
  if (nodos.some((n) => n.parentId === parentId)) return ordenar(nodos);
  return nodos.map((n) =>
    n.categoryId === parentId
      ? { ...n, children: ordenar(n.children) }
      : { ...n, children: conHermanasEnOrden(n.children, parentId, orden) },
  );
};

/**
 * Catálogo de un Store: modo y árbol de categorías (PIM Fase 18).
 *
 * **Es el otro scope del PIM y no se mezcla con el global.** Los productos, atributos y
 * conjuntos son del catálogo unificado; el árbol es de cada Store. El Store XCONS usa esta
 * misma pantalla — su árbol *es* el del catálogo unificado, que además comparten los 33
 * stores que apuntan a su root.
 *
 * **Qué se puede cambiar lo decide el nivel de `categorias` en este store** (xcons/xcons-docs#439):
 * `viewer` ve el árbol, `editor` además crea y edita, y `admin` además borra
 * (xcons/xcons-docs#444). El árbol de XCONS lo comparten
 * los 33 stores que apuntan a la root 2, así que `editor` y `admin` sobre ese árbol los tienen
 * sólo los operadores del Store XCONS: en los demás stores que lo comparten el backend no los
 * ofrece. Un store con árbol propio sí puede tenerlos, en cualquier modo (xcons-docs#440).
 * La pantalla no repite esa regla, sigue al nivel.
 *
 * Asignar productos a una categoría pide además `editor` o `admin` en el Catálogo: es
 * cambiar un producto, no el árbol.
 *
 * Se entra desde el menú del store (`/stores/categories/:storeCode`), y el ítem sólo aparece
 * con el permiso `categorias` de ese store.
 */
export const StoreCatalogPage: React.FC = () => {
  const { id: storeCode = "" } = useParams<{ id: string }>();
  const { t } = useTranslation();
  // Sale de la sesión, que se congela al login: un nivel recién cambiado se ve al re-loguear.
  const { data: identity } = useGetIdentity<any>();

  const [data, setData] = useState<StoreCatalogTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [seleccionada, setSeleccionada] = useState<number | null>(null);

  const [editando, setEditando] = useState<CategoryNode | null>(null);
  const [creandoBajo, setCreandoBajo] = useState<CategoryNode | null>(null);
  const [formCategoria] = Form.useForm();
  const [ejecutando, setEjecutando] = useState(false);
  /** Sube con cada recarga del árbol: el historial de la categoría la sigue (xcons-docs#436). */
  const [recargas, setRecargas] = useState(0);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<StoreCatalogTree>(
        `${urlDeStore(storeCode)}/categories`,
        {
          params: {
            search: search || undefined,
          },
        },
      );
      setData(data);
      setRecargas((n) => n + 1);
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
          t("storeCategories.loadError", "No se pudieron cargar las categorías del Store"),
      );
    } finally {
      setLoading(false);
    }
  }, [storeCode, search, t]);

  // Al cambiar de Store se descarta el árbol anterior: si el nuevo falla, no tiene que
  // quedar a la vista el de otro Store.
  useEffect(() => {
    setData(null);
    setSeleccionada(null);
  }, [storeCode]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const porId = useMemo(() => indexar(data?.nodes ?? []), [data]);
  const nodoSeleccionado = seleccionada ? porId.get(seleccionada) : undefined;
  const manual = data?.catalogMode === "manual_categories";
  const nivelCategorias = nivelDe(
    identity?.permissions?.store?.categorias?.[storeCode],
  );
  const nivelCatalogo = nivelDe(identity?.permissions?.global?.catalog?.product);
  // Editor crea y edita (xcons-docs#440); Admin además borra (xcons-docs#444). El API pide lo
  // mismo en cada ruta: esconder el botón no es el gate.
  const editable = nivelCategorias === "editor" || nivelCategorias === "admin";
  const puedeBorrar = nivelCategorias === "admin";
  // La raíz del store se renombra y nada más (xcons-docs#446).
  const editandoRaiz =
    !!editando && editando.categoryId === data?.rootCategoryId;
  // **Ver la lista de productos pide como mínimo Viewer for all en el Catálogo** y acceso a
  // Categorías (xcons-docs#470); antes pedía Editor, el mismo permiso que agregar o sacar.
  const nivelesQueVen = ["viewer", "by_relation", "editor", "admin"];
  const puedeVerProductos =
    nivelesQueVen.includes(nivelCatalogo) &&
    nivelCategorias !== "" &&
    nivelCategorias !== "no_access";
  // Agregar y sacar productos: Editor for all en el Catálogo **y** Editor en Categorías, como
  // mínimo (xcons-docs#470). El API pide lo mismo.
  const puedeEditarProductos =
    (nivelCatalogo === "editor" || nivelCatalogo === "admin") && editable;

  const guardarCategoria = async () => {
    const values = await formCategoria.validateFields();
    setEjecutando(true);
    try {
      if (creandoBajo) {
        await axiosInstance.post(`${urlDeStore(storeCode)}/categories`, {
          parentId: creandoBajo.categoryId,
          ...values,
        });
        message.success(t("storeCategories.created", "Categoría creada"));
      } else if (editando) {
        await axiosInstance.patch(
          `${urlDeStore(storeCode)}/categories/${editando.categoryId}`,
          // De la raíz, sólo el nombre: el botón Editar precarga el resto en el form.
          editandoRaiz ? { name: values.name } : values,
        );
        message.success(t("storeCategories.updated", "Categoría actualizada"));
      }
      setCreandoBajo(null);
      setEditando(null);
      formCategoria.resetFields();
      await cargar();
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? t("storeCategories.saveError", "No se pudo guardar"),
      );
    } finally {
      setEjecutando(false);
    }
  };

  const borrar = (nodo: CategoryNode) =>
    Modal.confirm({
      title: `${t("storeCategories.delete", "Borrar")} "${nodo.name}"`,
      okText: t("storeCategories.delete", "Borrar"),
      okButtonProps: { danger: true },
      cancelText: t("buttons.cancel", "Cancelar"),
      // `children_count` cuenta toda la descendencia, no las hijas directas: el número que
      // se le muestra al operador es el de la rama entera, que es lo que se borra.
      content: nodo.childrenCount
        ? t("storeCategories.deleteWithChildren", {
            count: nodo.childrenCount,
            defaultValue:
              "Se borra la categoría y las {{count}} subcategorías que tiene debajo. No se puede deshacer.",
          })
        : t(
            "storeCategories.deleteConfirm",
            "Se borra la categoría. No se puede deshacer.",
          ),
      onOk: async () => {
        try {
          const { data } = await axiosInstance.delete<{ deleted: number }>(
            `${urlDeStore(storeCode)}/categories/${nodo.categoryId}`,
          );
          message.success(
            `${data.deleted} ${
              data.deleted === 1
                ? t("storeCategories.deletedOne", "categoría borrada")
                : t("storeCategories.deletedMany", "categorías borradas")
            }`,
          );
          setSeleccionada(null);
          await cargar();
        } catch (e) {
          const res = (
            e as { response?: { data?: { message?: string; code?: string } } }
          )?.response;
          if (res?.data?.code === "CATEGORY_HAS_PRODUCTS") {
            // No es un error del operador: es la rama que todavía tiene productos. El
            // mensaje del backend trae el conteo.
            message.warning(res.data.message!);
          } else {
            message.error(
              res?.data?.message ??
                t("storeCategories.deleteError", "No se pudo borrar"),
            );
          }
        }
      },
    });

  /**
   * Reordenar entre hermanas arrastrando (xcons-docs#455): sólo con permiso para editar el
   * árbol, y nunca con el buscador puesto, porque el árbol filtrado no
   * muestra todas las hermanas y la posición sería ambigua. La raíz no se mueve.
   */
  const puedeReordenar = editable && !search;

  const hermanasDe = (nodo: CategoryNode): CategoryNode[] =>
    porId.get(nodo.parentId)?.children ??
    (data?.nodes ?? []).filter((n) => n.parentId === nodo.parentId);

  const mover = (nodo: CategoryNode, destino: CategoryNode, despues: boolean) => {
    const sinElla = hermanasDe(nodo)
      .map((n) => n.categoryId)
      .filter((id) => id !== nodo.categoryId);
    const i = sinElla.indexOf(destino.categoryId);
    if (i < 0) return Promise.resolve();
    return moverA(nodo, despues ? i + 1 : i);
  };

  /**
   * Lleva la categoría al índice `position` entre sus hermanas (0 = primera). Lo usa el
   * arrastre; los botones Subir y Bajar de xcons-docs#470 se sacaron en xcons-docs#490.
   */
  const moverA = async (nodo: CategoryNode, position: number) => {
    if (!data) return;
    const orden = hermanasDe(nodo)
      .map((n) => n.categoryId)
      .filter((id) => id !== nodo.categoryId);
    if (position < 0 || position > orden.length) return;
    orden.splice(position, 0, nodo.categoryId);
    // Soltarla donde estaba no es un cambio.
    if (hermanasDe(nodo).map((n) => n.categoryId).join() === orden.join()) return;

    const guardar = async () => {
      const anterior = data;
      setData({ ...data, nodes: conHermanasEnOrden(data.nodes, nodo.parentId, orden) });
      try {
        await axiosInstance.post(
          `${urlDeStore(storeCode)}/categories/${nodo.categoryId}/move`,
          { position },
        );
        message.success(t("storeCategories.moved", "Orden actualizado"));
        await cargar();
      } catch (e) {
        // Vuelve al orden de antes: el árbol no puede mostrar algo que no se guardó.
        setData(anterior);
        message.error(
          (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? t("storeCategories.moveError", "No se pudo cambiar el orden"),
        );
      }
    };

    // Sin confirmar, también en el árbol de XCONS: arrastrar ya es la intención, y si no
    // era, se vuelve a arrastrar (Diseño, xcons-docs#455).
    await guardar();
  };

  const aTreeData = (nodos: CategoryNode[]): any[] =>
    nodos.map((n) => ({
      key: n.categoryId,
      title: (
        <Space size={4}>
          {/* La manija que dice que se puede arrastrar (xcons-docs#455): sin ella el
              arrastre existía pero no se veía. La raíz no se mueve y no la lleva. */}
          {puedeReordenar && n.categoryId !== data?.rootCategoryId && (
            <Tooltip title={t("storeCategories.dragHint", "Arrastrá para cambiar el orden")}>
              <HolderOutlined style={{ color: "#8c8c8c", cursor: "grab" }} />
            </Tooltip>
          )}
          <Text strong={!n.isActive ? false : true} type={n.isActive ? undefined : "secondary"}>
            {n.name}
          </Text>
          {/* `product_count` cuenta sólo los asignados directo, no los de la rama: una
              madre puede mostrar menos que una hija, y el tooltip lo dice. */}
          {n.productCount > 0 && (
            <Tooltip
              title={t(
                "storeCategories.productCountHint",
                "Productos asignados directo a esta categoría, sin contar sus subcategorías",
              )}
            >
              <Tag>{n.productCount.toLocaleString("es-AR")}</Tag>
            </Tooltip>
          )}
          {!n.isActive && (
            <Tag color="default">
              {t("storeCategories.inactive", "Inactiva")}
            </Tag>
          )}
          {/* Sin etiqueta "Fuera del menú" (Diseño, xcons-docs#455): si está en el menú se
              ve y se cambia en Editar. */}
        </Space>
      ),
      children: aTreeData(n.children),
    }));

  const titulo = (
    <Title level={3} style={{ display: "flex", alignItems: "center" }}>
      <FolderOpenOutlined style={{ marginRight: "10px" }} />
      {t("smartMenu.storeLabel.categories", "Categorías")}
    </Title>
  );

  if (loading && !data) {
    return (
      <>
        {titulo}
        <Card>
          <Skeleton active paragraph={{ rows: 10 }} />
        </Card>
      </>
    );
  }

  // Sin datos no se sabe el modo ni si hay categorías: mostrar los carteles de siempre
  // sería afirmar un "modo automático" y un árbol vacío que nadie verificó (pasa con un
  // 403 de un Store sin permiso).
  if (!data) {
    return (
      <>
        {titulo}
        <Card>
          <Empty
            description={t(
              "storeCategories.loadError",
              "No se pudieron cargar las categorías del Store",
            )}
          >
            <Button onClick={() => void cargar()}>
              {t("storeCategories.retry", "Reintentar")}
            </Button>
          </Empty>
        </Card>
      </>
    );
  }

  return (
    <>
      {titulo}
      <Flex vertical gap={16}>
        {/* El modo se muestra, no se cambia desde acá: pasar un Store a manual cambia
            qué vende —medido, hay stores que perderían más del 90% del catálogo— y es
            una decisión del PM, no de quien administra las categorías. Un solo aviso por
            caso: el cartel es el que explica el modo, sin etiquetas que lo repitan. */}
        {manual && (
          <Alert
            type="info"
            showIcon
            message={t(
              "storeCategories.manualTitle",
              "Modo manual: el Store vende lo que está en sus categorías",
            )}
            description={t(
              "storeCategories.manualDescription",
              "Sólo se venden los productos asignados a alguna de estas categorías. Las categorías también arman el menú del storefront.",
            )}
          />
        )}
        {/* El modo automático no lleva cartel, ni con árbol propio ni con el de XCONS
            (xcons-docs#449, #466, #499). */}

        {/* El orden de la pantalla es fijo (xcons-docs#449): el árbol con su buscador en el
            encabezado, abajo los productos de la categoría elegida y al final el historial. */}
        <Card
          style={TARJETA_CON_ENCABEZADO.style}
          styles={{ header: TARJETA_CON_ENCABEZADO.header, body: { padding: 16 } }}
          title={
            // El buscador del árbol va en el encabezado, al lado del nombre, en el lugar
            // de la URL (xcons-docs#466). Del alto de los botones, y separado de ellos.
            <Flex
              align="center"
              gap={24}
              style={{ width: "100%", paddingInlineEnd: 16 }}
            >
              <Text style={{ ...TARJETA_CON_ENCABEZADO.titulo, flexShrink: 0 }}>
                {nodoSeleccionado
                  ? nodoSeleccionado.name
                  : t("storeCategories.pickTitle", "Elegí una categoría")}
              </Text>
              <Input
                style={{ flex: 1, minWidth: 0 }}
                allowClear
                prefix={<SearchOutlined style={{ color: "#999" }} />}
                placeholder={t(
                  "storeCategories.searchPlaceholder",
                  "Buscar categoría",
                )}
                defaultValue={search}
                onPressEnter={(e) => setSearch(e.currentTarget.value)}
                // Vaciarlo, con la cruz o borrando, vuelve al árbol entero, como antes.
                onChange={(e) => !e.target.value && setSearch("")}
              />
            </Flex>
          }
          extra={
            nodoSeleccionado && (
              // Del tamaño de los botones principales (xcons-docs#466). Sin permiso se ven
              // igual, deshabilitados, en vez de un texto que lo explique (xcons-docs#499).
              <Space wrap>
                <Button
                  icon={<PlusOutlined />}
                  disabled={!editable}
                  onClick={() => {
                    setCreandoBajo(nodoSeleccionado);
                    formCategoria.resetFields();
                  }}
                >
                  {t("storeCategories.newChild", "Nueva categoría")}
                </Button>
                {/* Sin Subir ni Bajar (xcons-docs#490): el orden se cambia arrastrando. */}
                {/* Editar y Borrar, sólo el ícono y en negro (xcons-docs#466): el nombre va
                    en el tooltip. */}
                <Tooltip title={t("buttons.edit", "Editar")}>
                  <Button
                    aria-label={t("buttons.edit", "Editar")}
                    icon={<EditOutlined style={editable ? { color: "#000" } : undefined} />}
                    disabled={!editable}
                    onClick={() => {
                      setEditando(nodoSeleccionado);
                      formCategoria.setFieldsValue({
                        name: nodoSeleccionado.name,
                        urlKey: nodoSeleccionado.urlKey,
                        isActive: nodoSeleccionado.isActive,
                        includeInMenu: nodoSeleccionado.includeInMenu,
                      });
                    }}
                  />
                </Tooltip>
                {/* La raíz del store viene en el árbol —es de donde cuelga el primer
                    nivel— pero no se borra: el API la rechaza. Borrar es de Admin. */}
                {nodoSeleccionado.categoryId !== data.rootCategoryId && (
                  <Tooltip title={t("storeCategories.delete", "Borrar")}>
                    <Button
                      aria-label={t("storeCategories.delete", "Borrar")}
                      icon={<DeleteOutlined style={puedeBorrar ? { color: "#000" } : undefined} />}
                      disabled={!puedeBorrar}
                      onClick={() => borrar(nodoSeleccionado)}
                    />
                  </Tooltip>
                )}
              </Space>
            )
          }
        >
          {data.nodes.length ? (
            <Tree
              showLine
              blockNode
              defaultExpandedKeys={data.nodes.map((n) => n.categoryId)}
              treeData={aTreeData(data.nodes)}
              selectedKeys={seleccionada ? [seleccionada] : []}
              onSelect={(keys) => setSeleccionada(Number(keys[0]) || null)}
              draggable={
                puedeReordenar && {
                  icon: false,
                  nodeDraggable: (n) => Number(n.key) !== data.rootCategoryId,
                }
              }
              // Sólo los huecos entre hermanas: ni adentro de otra (posición 0) ni en otro nivel.
              allowDrop={({ dragNode, dropNode, dropPosition }) =>
                dropPosition !== 0 &&
                porId.get(Number(dragNode.key))?.parentId ===
                  porId.get(Number(dropNode.key))?.parentId
              }
              onDrop={(info) => {
                const nodo = porId.get(Number(info.dragNode.key));
                const destino = porId.get(Number(info.node.key));
                if (!nodo || !destino || !info.dropToGap) return;
                // antd da la posición absoluta; relativa al nodo destino es -1 (antes) o 1 (después).
                const pos = info.node.pos.split("-");
                const relativa = info.dropPosition - Number(pos[pos.length - 1]);
                void mover(nodo, destino, relativa > 0);
              }}
            />
          ) : (
            <Empty
              description={
                search
                  ? `${t("storeCategories.noMatch", "Ninguna categoría coincide con")} "${search}"`
                  : t(
                      "storeCategories.empty",
                      "Este Store todavía no tiene categorías",
                    )
              }
            />
          )}
        </Card>

        {/* Los productos de la categoría, abajo del árbol apenas se la elige, sin un botón
            "Productos" de por medio (xcons-docs#449). Asignar productos pide además
            `editor` o `admin` en el Catálogo: sin eso no aparecen, igual que antes no
            aparecía el botón que los abría. */}
        {nodoSeleccionado && puedeVerProductos && (
          <ProductosDeLaCategoria
            storeCatalogUrl={urlDeStore(storeCode)}
            categoria={nodoSeleccionado}
            arbolCompartido={data.usesDefaultTree}
            recarga={recargas}
            onCambio={cargar}
            puedeEditar={puedeEditarProductos}
          />
        )}

        {/* El historial siempre a la vista y al final, como en las demás secciones
            (xcons-docs#449): el de la categoría elegida, y sin ninguna elegida el de la raíz
            del Store. Se vuelve a pedir con cada recarga del árbol, que es lo que sigue a
            guardar, asignar, desasignar o borrar (xcons-docs#436). */}
        <HistorialDelCatalogo
          module="category"
          entityId={seleccionada ?? data.rootCategoryId}
          recarga={recargas}
        />
      </Flex>

      <Modal
        open={!!creandoBajo || !!editando}
        title={
          creandoBajo
            ? `${t("storeCategories.newChildIn", "Nueva subcategoría en")} "${creandoBajo.name}"`
            : editandoRaiz
              ? t("storeCategories.editRootTitle", "Renombrar la categoría raíz")
              : t("storeCategories.editTitle", "Editar categoría")
        }
        okText={t("buttons.save", "Guardar")}
        cancelText={t("buttons.cancel", "Cancelar")}
        confirmLoading={ejecutando}
        onOk={() => void guardarCategoria()}
        onCancel={() => {
          setCreandoBajo(null);
          setEditando(null);
        }}
      >
        <Form form={formCategoria} layout="vertical">
          <Form.Item
            name="name"
            label={t("storeCategories.name", "Nombre")}
            rules={[
              {
                required: true,
                message: t(
                  "storeCategories.nameRequired",
                  "El nombre es obligatorio",
                ),
              },
            ]}
          >
            <Input placeholder="Bulones" />
          </Form.Item>
          {/* De la raíz sólo se cambia el nombre (xcons-docs#446): no tiene URL propia, y
              desactivarla o sacarla del menú apaga el árbol entero. El API rechaza el resto
              con ROOT_CATEGORY_NAME_ONLY; sin los campos, el form ni los manda. */}
          {!editandoRaiz && (
            <>
              <Form.Item
                name="urlKey"
                label={t("storeCategories.url", "URL")}
                extra={
                  editando
                    ? t(
                        "storeCategories.urlEditHint",
                        "Si la cambiás, también cambian las URLs de sus subcategorías. Las URLs viejas redirigen a las nuevas.",
                      )
                    : t(
                        "storeCategories.urlNewHint",
                        "Si la dejás vacía, se arma a partir del nombre. No puede repetirse con otra categoría del mismo nivel.",
                      )
                }
              >
                <Input placeholder="bulones" />
              </Form.Item>
              <Form.Item
                name="isActive"
                label={t("storeCategories.active", "Activa")}
                valuePropName="checked"
              >
                <Switch defaultChecked />
              </Form.Item>
              <Form.Item
                name="includeInMenu"
                label={t("storeCategories.inMenu", "En el menú del storefront")}
                valuePropName="checked"
              >
                <Switch defaultChecked />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};
